import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableScrollContainer } from "../../atom/DraggableScrollContainer";
import { SectionHeading } from "../../atom/SectionHeading";
import { CornerUser } from "../../atom/CornerUser";
import { Button } from "../../atom/Button";
import { UploadTrainingModal } from "./UploadTrainingModal";
import { TrainingTable } from "./TrainingTable";
import { useResource, usePostResource } from "../../../services";
import { getTrainingDocumentStatus, getTrainingDocumentType } from "../../../services/apiHelpers";
import { createResourceUrl } from "../../../utils/data";
import { useUserContext } from "../../../utils/contexts/UserProvider";
import {
  ServerFile,
  TrainingDocument,
  ProfessionalTraining as ProfessionalTrainingType,
} from "../../../types/components";
import { successToast } from "../../../utils/toast";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { ProfessionalTrainingOverview } from "../../atom/ProfessionalTrainingOverview";

export const ProfessionalTraining = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: professionalTrainings } = useResource<ProfessionalTrainingType[]>(
    createResourceUrl("/common/professional-training", {
      include: ["status"],
      filters: {
        "eq:year": DateTime.now().year,
        "eq:user_id": user?.id,
      },
    }),
    user,
  );

  const professionalTraining = professionalTrainings?.[0];

  const { data: rawData, mutate } = useResource<TrainingDocument[]>(
    createResourceUrl("/common/training-document", {
      include: ["status", "file"],
      filters: {
        "eq:user_id": user?.id,
      },
    }),
    user,
  );

  const { updateResource: createTrainingDocument } = usePostResource("/common/training-document");

  const { updateResource: triggerUpload } = usePostResource("/file/upload/new");
  const handleUpload = async (file: File, year: string, hours: string) => {
    const formData = new FormData();
    formData.append("UploadFiles", file);

    const uploadedResponse: { data: ServerFile } = await triggerUpload(formData);
    const uploadedFileId = uploadedResponse?.data?.id;

    if (uploadedFileId) {
      const statusResponse = await getTrainingDocumentStatus("waiting_for_approval");
      const statusId = statusResponse?.[0]?.id;

      if (!statusId) {
        return;
      }

      const type = await getTrainingDocumentType("training_certificate");

      const payload = {
        user_id: user?.id,
        file_id: uploadedFileId,
        id: self.crypto.randomUUID(),
        status_id: statusId,
        name: file.name,
        type_id: type?.[0]?.id,
        year: parseInt(year),
        hours: parseInt(hours),
      };

      const result = await createTrainingDocument(payload);

      if (!result) {
        toast.error("Upload failed");
      }

      successToast(t("File uploaded successfully"));
      mutate();
    }
  };

  const tableData: TrainingDocument[] | undefined = rawData ?? undefined;

  return (
    <div className="flex flex-col flex-1 items-center w-full flex-grow">
      <div className="w-full overflow-hidden flex flex-col flex-grow">
        <DraggableScrollContainer className="m-[20px] overflow-y-auto select-none ml-[8px] bg-gray-100 flex-grow">
          <div className="flex flex-col gap-10">
            <div className="px-12 pt-10">
              <SectionHeading
                title={t("Professional Training")}
                subtitle={t("Manage your certificates and professional development.")}
                subtitleClassName="max-w-[560px]"
                cornerComponent={<CornerUser />}
              />
            </div>

            <div className="flex gap-12 justify-between px-12">
              <ProfessionalTrainingOverview
                year={professionalTraining?.year}
                hoursAccepted={professionalTraining?.hours_accepted}
                statusName={professionalTraining?.status?.name}
              />
              <div className="mt-6 w-fit self-end">
                <Button label={t("Upload")} onClick={() => setIsModalOpen(true)} type="primary" />
              </div>
            </div>

            <div className="flex flex-col bg-white ml-12 mr-[52px] mb-[75px] rounded-[6px] shadow">
              <TrainingTable data={tableData} />
            </div>
            <div className="flex-grow"></div>
          </div>
        </DraggableScrollContainer>

        <UploadTrainingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUpload={handleUpload}
        />
      </div>
    </div>
  );
};
